import tw from 'twin.macro'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { useClickAnyWhere, useEventListener } from 'usehooks-ts'

import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import ImageContentful from '@/components/ui/image-contentful'

import { getBrowserName, getOs } from '@/utils/user-info'

type Browser = {
  name: string
  url: string
  whyChooseUrl: string
  icon: string
  unicode: string
  description: string
  image: string
}

type DownloadTabsProps = {
  id?: string
  headline?: string
  description?: string
  browsers: Browser[]
  type?: 'browsers' | 'os'
  disclaimer?: string
}

export default function DownloadTabs({
  id,
  headline,
  description,
  browsers = [],
  type = 'browsers',
  disclaimer,
}: DownloadTabsProps) {
  const [currentTab, setCurrentTab] = useState<string | null>(null)
  const [tabList, setTabList] = useState<Browser[]>(browsers)
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!type || type === 'browsers') {
      const browser = getBrowserName()
      if (browser !== 'Unknown') {
        const browserIndex = tabList.findIndex((b) => b.name === browser)
        if (browserIndex !== -1) {
          setTabList([tabList[browserIndex], ...tabList.filter((b) => b.name !== browser)])
        }
      }
      setCurrentTab(browser)
    } else if (type === 'os') {
      const os = getOs()
      if (os !== 'Unknown') {
        const osIndex = tabList.findIndex((b) => b.name === os)
        if (osIndex !== -1) {
          setTabList([tabList[osIndex], ...tabList.filter((b) => b.name !== os)])
        }
        setCurrentTab(os)
      }
    }
  }, [])

  const [hideOutline, setHideOutline] = useState(true)
  useClickAnyWhere(() => setHideOutline(true))
  useEventListener('keydown', () => {
    setHideOutline(false)
  })

  const tabListRef = useRef<HTMLDivElement>()
  const motionDirection = useRef<'left' | 'right'>('left')
  const [tabIndex, setTabIndex] = React.useState(0)

  const handleTabChange = (index: number) => {
    motionDirection.current = index > tabIndex ? 'left' : 'right'
    setTabIndex(index)
  }

  const handleTabClick = () => {
    if (tabListRef.current) {
      const header = document.querySelector('header')
      const { y } = tabListRef.current.getBoundingClientRect()
      window.scrollBy({ top: y - header.offsetHeight - 12, behavior: 'smooth' })
    }
  }

  const selectedTab = tabList[tabIndex]

  return (
    <section
      id={id}
      tw="max-w-8xl mx-auto px-5 lg:px-9 transition-opacity duration-100"
      css={[currentTab === null ? tw`opacity-0` : tw`opacity-100`]}
    >
      {(headline || description) && (
        <div tw="mb-12">
          {headline && <h3 tw="text-2xl lg:text-4xl text-center mb-4">{headline}</h3>}
          {description && <p tw="text-base text-center mb-0">{description}</p>}
        </div>
      )}
      <TabGroup key={currentTab} tw="flex flex-col items-center" onChange={handleTabChange} manual>
        <TabList
          ref={tabListRef}
          tw="bg-[#DBE5F6] rounded-3xl md:px-4 2xl:px-0 2xl:rounded-full inline-flex flex-wrap items-center justify-center max-w-[calc(clamp(40%, 800px,100%))] 2xl:max-w-none mx-auto gap-1 mb-6"
        >
          {tabList.map((browser) => (
            <Tab as={Fragment} key={browser.icon}>
              {({ hover, selected }) => (
                <button
                  type="button"
                  tw="py-2 px-3 md:px-6 md:py-3 border-2 border-transparent rounded-full text-primaryBlue flex items-center transition-colors outline-none focus:ring-4 focus:ring-primaryBlue focus:ring-opacity-50"
                  css={[
                    selected && tw`bg-primaryBlue border-primaryBlue text-white`,
                    hover && tw`border-primaryBlue`,
                    hideOutline && tw`focus:ring-0`,
                  ]}
                  onClick={handleTabClick}
                >
                  <span
                    tw="font-bwi leading-[2rem] text-[2rem] mr-1 md:mr-2"
                    data-name={`bwi-${browser.icon}`}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: browser.unicode }}
                  />
                  {browser.name}
                </button>
              )}
            </Tab>
          ))}
        </TabList>
        <TabPanels tw="w-full">
          <AnimatePresence mode="wait" initial={false}>
            <TabPanel
              key={tabIndex}
              static
              tw="w-full bg-gray-100 border-gray-200 text-gray-800 rounded-[20px]"
              as={motion.div}
              initial="hide"
              animate="show"
              exit="exit"
              custom={motionDirection.current}
              variants={{
                hide: (direction: typeof motionDirection.current) => ({
                  opacity: 0,
                  x: direction === 'left' ? '20%' : '-20%',
                  transition: {
                    duration: 0.1,
                    ease: 'easeOut',
                  },
                }),
                show: {
                  opacity: 1,
                  x: 0,
                  transition: {
                    duration: 0.3,
                    ease: 'anticipate',
                  },
                },
                exit: (direction: typeof motionDirection.current) => ({
                  opacity: 0,
                  x: direction === 'left' ? '-20%' : '20%',
                  transition: {
                    duration: 0.1,
                    ease: 'easeOut',
                  },
                }),
              }}
            >
              <div tw="flex flex-col-reverse lg:flex-row gap-4 p-4">
                <div tw="flex-grow flex flex-col justify-center px-6 md:px-16 lg:max-w-1/2">
                  <div tw="flex items-center gap-3 mb-2 md:mb-6">
                    <span
                      tw="font-bwi text-[3rem]"
                      data-name={`bwi-${selectedTab.icon}`}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: selectedTab.unicode }}
                    />
                    <h3 tw="mb-0">{selectedTab.name}</h3>
                  </div>
                  {/* eslint-disable-next-line react/no-danger */}
                  <p tw="text-lg mb-6" dangerouslySetInnerHTML={{ __html: selectedTab.description }} />
                  {(selectedTab.url || selectedTab.whyChooseUrl) && (
                    <div tw="flex flex-col xl:flex-row gap-4">
                      {selectedTab.url && (
                        <div>
                          <CtaLink
                            className={`self-start plausible-event-name=download+click plausible-event-button=browser+${selectedTab.name.toLocaleLowerCase()}`}
                            to={selectedTab.url}
                            variant={buttonStyles.btnOutlined}
                          >
                            Download now
                          </CtaLink>
                        </div>
                      )}
                      {selectedTab.whyChooseUrl && (
                        <div>
                          <CtaLink
                            className={`self-start plausible-event-name=why-choose-bitwarden+click plausible-event-button=browser+${selectedTab.name.toLocaleLowerCase()}`}
                            to={selectedTab.whyChooseUrl}
                            variant={buttonStyles.btnOutlined}
                          >
                            Why Choose Bitwarden
                          </CtaLink>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {selectedTab.image && (
                  <ImageContentful
                    tw="object-contain object-top max-h-96 lg:max-h-none lg:max-w-1/2"
                    publicUrl={selectedTab.image}
                    file={{ url: selectedTab.image, contentType: 'image/svg+xml' }}
                    title={selectedTab.name}
                  />
                )}
              </div>
            </TabPanel>
          </AnimatePresence>
        </TabPanels>
      </TabGroup>
      {disclaimer && <p tw="text-lg text-center mt-6 mx-auto text-gray-600">{disclaimer}</p>}
    </section>
  )
}
